import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Check, 
  Star, 
  TrendingUp, 
  Building2, 
  Brain, 
  MapPin, 
  ArrowRight,
  ChevronDown,
  ChevronUp,
  X as LucideX,
  Clock,
  ArrowRight as LucideArrowRight,
  DollarSign,
  User,
  Link as LucideLink,
  BarChart3
} from 'lucide-react';
import { AnimatedNumber } from '../components/AnimatedNumber';
import { TopNav } from '../components/TopNav';
import { Footer } from '../components/Footer';

interface PricingPlan {
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  features: string[];
  popular?: boolean;
  cta: string;
}

const pricingPlans: PricingPlan[] = [
  {
    name: 'Free',
    monthlyPrice: 0,
    annualPrice: 0,
    features: [
      'Track 2 properties',
      '5 financial accounts',
      'Basic net worth tracking',
      'Monthly summary',
      'Basic dashboard'
    ],
    cta: 'Get Started Free'
  },
  {
    name: 'Pro',
    monthlyPrice: 15,
    annualPrice: 96,
    features: [
      'Track 5 properties',
      '10 financial accounts',
      'AI financial insights',
      'Dividend tracking',
      'Real estate ROI analysis',
      'Smart alerts & notifications',
      'Export tools',
      'Advanced analytics'
    ],
    popular: true,
    cta: 'Start Pro Trial'
  },
  {
    name: 'Elite',
    monthlyPrice: 29,
    annualPrice: 240,
    features: [
      'Unlimited properties',
      'Unlimited accounts',
      'Advanced dividend tracking & analysis',
      'Business income & expense tracking',
      'Multi-currency support',
      'Advanced real estate analytics',
      'Portfolio rebalancing insights',
      'Custom financial goal tracking'
    ],
    cta: 'Start Elite Trial'
  }
];

const testimonials = [
  {
    name: 'Sarah Chen',
    role: 'Real Estate Investor',
    content: 'SimpleTracker helped me see the real ROI on my properties. The AI insights are game-changing.',
    rating: 5
  },
  {
    name: 'Mike Rodriguez',
    role: 'Small Business Owner',
    content: 'Finally, a tool that makes sense of all my finances. The dashboard is exactly what I needed.',
    rating: 5
  },
  {
    name: 'Emily Watson',
    role: 'Financial Advisor',
    content: 'I recommend SimpleTracker to all my clients. It\'s the perfect balance of simplicity and power.',
    rating: 5
  }
];

const faqs = [
  {
    question: 'Is my financial data secure?',
    answer: 'Yes, we use bank-level encryption and never store your banking credentials. Your data is protected with the highest security standards.'
  },
  {
    question: 'Can I cancel my subscription anytime?',
    answer: 'Absolutely. You can cancel your subscription at any time with no questions asked. We also offer a 30-day money-back guarantee.'
  },
  {
    question: 'Do you support Canadian banks and institutions?',
    answer: 'Yes! SimpleTracker is built specifically for Canadians with support for all major Canadian banks and financial institutions.'
  },
  {
    question: 'Can I upgrade or downgrade my plan?',
    answer: 'Yes, you can change your plan at any time. Upgrades take effect immediately, and downgrades take effect at your next billing cycle.'
  },
  {
    question: 'What if I need help getting started?',
    answer: 'We offer free onboarding support for all users. Pro and Elite users get priority support and personalized setup assistance.'
  }
];

const NUMBER_OF_USERS = 51862;
const AVERAGE_NET_WORTH = 940000; // CAD
const TOTAL_ASSETS_TRACKED = NUMBER_OF_USERS * AVERAGE_NET_WORTH;

export const LandingPage: React.FC = () => {
  const [isAnnual, setIsAnnual] = useState(true);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/login?signup=true');
  };

  // Animated headline word cycling
  const animatedWords = [
    'confidence',
    'clarity',
    'control',
    'growth',
    'freedom',
  ];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentWordIndex((prev) => (prev + 1) % animatedWords.length);
        setIsAnimating(false);
      }, 300); // duration of the fade animation
    }, 4000);
    return () => clearInterval(interval);
  }, [animatedWords.length]);

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  return (
    <div className="min-h-screen bg-white">
      <TopNav />
      {/* Hero Section */}
      <section className="relative bg-white py-10 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="text-left">
              <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full text-xs sm:text-sm font-medium mb-8">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                Trusted by{' '}
                <span style={{ margin: '0 0.25em' }}>
                  <AnimatedNumber value={51862} duration={1200} />
                </span>
                Canadians
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                <span className="block text-2xl sm:text-4xl md:text-6xl font-bold text-gray-900 mb-1 leading-tight">Track Your Finances with{' '}</span>
                <span
                  className={`text-brand-500 inline-block transition-all duration-300 ease-in-out ${isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
                  style={{ minWidth: 120, display: 'inline-block' }}
                  aria-live="polite"
                >
                  {animatedWords[currentWordIndex].charAt(0).toUpperCase() + animatedWords[currentWordIndex].slice(1)}
                </span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 max-w-2xl">
                SimpleTracker gives you real-time insights into your net worth, property ROI, and financial goals. 
                No more spreadsheets, just smart financial intelligence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 w-full max-w-xs sm:max-w-none">
                <button
                  onClick={handleGetStarted}
                  className="bg-brand-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-brand-600 transition-colors flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 group w-full sm:w-auto"
                >
                  Get Started Free
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <a
                  href="#pricing"
                  className="border border-green-300 text-green-700 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-50 transition-colors hover:border-green-400 w-full sm:w-auto text-center"
                >
                  View Pricing
                </a>
              </div>
              
              {/* Trust indicators */}
              <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-4 sm:gap-8 text-xs sm:text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Bank-level security</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span><AnimatedNumber value={51862} duration={1200} /> active users</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>30-day money-back guarantee</span>
                </div>
              </div>
            </div>
            
            {/* Mockup/Demo Section */}
            <div className="relative mt-10 sm:mt-12 lg:mt-0 max-w-xs sm:max-w-md md:max-w-lg mx-auto w-full">
              <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-8 border border-gray-100 w-full">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                  <div className="text-sm text-gray-500">SimpleTracker Dashboard</div>
                </div>
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-green-50 to-brand-50 rounded-lg p-3 sm:p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-sm text-gray-600">Total Net Worth</div>
                        <div className="text-2xl font-bold text-gray-900">$847,392</div>
                      </div>
                      <div className="text-green-600 text-sm font-medium">+12.4%</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                      <div className="text-xs text-gray-500">Properties</div>
                      <div className="text-lg font-semibold text-gray-900">$650K</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                      <div className="text-xs text-gray-500">Investments</div>
                      <div className="text-lg font-semibold text-gray-900">$197K</div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 bg-green-100 rounded-lg p-2 sm:p-3 shadow-lg text-xs sm:text-sm">
                <div className="text-xs text-green-700 font-medium">AI Insight</div>
                <div className="text-sm text-green-800">Property ROI: 8.2%</div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-brand-100 rounded-lg p-2 sm:p-3 shadow-lg text-xs sm:text-sm">
                <div className="text-xs text-brand-700 font-medium">Goal Progress</div>
                <div className="text-sm text-brand-800">85% to $1M</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section - The Hook */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-medium mb-4">
              The Problem
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Most Canadians Are Flying Blind With Their Finances
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              You're working hard, saving money, and investing in properties, but you have no clear picture of your true net worth or whether your investments are actually working for you.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex justify-center mb-4">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-500 shadow-md">
                  <LucideX className="w-7 h-7 text-white" strokeWidth={3} />
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Spreadsheet Hell</h3>
              <p className="text-gray-600 text-center">
                Spending hours updating Excel sheets that are outdated before you finish. One wrong formula and your entire financial picture is wrong.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex justify-center mb-4">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-500 shadow-md">
                  <LucideX className="w-7 h-7 text-white" strokeWidth={3} />
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">No Real ROI Data</h3>
              <p className="text-gray-600 text-center">
                Your properties might be making money, but you don't know your actual CAP rates, cash-on-cash returns, or total portfolio performance.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex justify-center mb-4">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-500 shadow-md">
                  <LucideX className="w-7 h-7 text-white" strokeWidth={3} />
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Missing the Big Picture</h3>
              <p className="text-gray-600 text-center">
                You can't see how all your assets work together. Is your real estate portfolio actually growing your wealth faster than the stock market?
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section - The Promise */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-brand-100 text-brand-700 rounded-full text-sm font-medium mb-4">
              The Solution
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              SimpleTracker Gives You Crystal Clear Financial Intelligence
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              One dashboard that shows your true net worth, property performance, and AI-powered insights to make smarter financial decisions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg border border-brand-100">
              <div className="flex justify-center mb-4">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-500 shadow-md">
                  <Check className="w-7 h-7 text-white" strokeWidth={3} />
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Real-Time Net Worth</h3>
              <p className="text-gray-600 text-center">
                See your exact net worth updated in real-time. No more guessing or outdated spreadsheets. Know exactly where you stand financially.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg border border-brand-100">
              <div className="flex justify-center mb-4">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-500 shadow-md">
                  <Check className="w-7 h-7 text-white" strokeWidth={3} />
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Property ROI Calculator</h3>
              <p className="text-gray-600 text-center">
                Calculate actual CAP rates, cash-on-cash returns, and total ROI for each property. Know which investments are truly performing.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg border border-brand-100">
              <div className="flex justify-center mb-4">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-500 shadow-md">
                  <Check className="w-7 h-7 text-white" strokeWidth={3} />
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">AI Financial Insights</h3>
              <p className="text-gray-600 text-center">
                Get intelligent recommendations on how to optimize your portfolio, reduce taxes, and reach your financial goals faster.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-4">
              Features
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Track Your Wealth
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful tools designed specifically for Canadian investors and property owners
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                <TrendingUp className="w-8 h-8 text-green-600 group-hover:scale-110 transition-transform" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Track Everything
              </h3>
              <p className="text-gray-600">
                Monitor all your assets and liabilities in one unified dashboard
              </p>
            </div>
            
            <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                <Building2 className="w-8 h-8 text-green-600 group-hover:scale-110 transition-transform" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Property ROI
              </h3>
              <p className="text-gray-600">
                Calculate CAP rates and ROI for your real estate investments
              </p>
            </div>
            
            {/* AI Insights Feature */}
            <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                <Brain className="w-8 h-8 text-green-600 group-hover:scale-110 transition-transform" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                AI Insights
              </h3>
              <p className="text-gray-600">
                Get intelligent recommendations and trend analysis
              </p>
            </div>
            
            <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                <svg className="w-8 h-8 text-green-600 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Budgeting
              </h3>
              <p className="text-gray-600">
                Create and track monthly budgets by category. Get alerts when you're approaching limits.
              </p>
            </div>
            
            <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                <svg className="w-8 h-8 text-green-600 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Goal Setting
              </h3>
              <p className="text-gray-600">
                Set and track financial goals like retirement or property purchases with personalized timelines.
              </p>
            </div>
            
            <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                <svg className="w-8 h-8 text-green-600 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Cash Flow Analysis
              </h3>
              <p className="text-gray-600">
                Monitor your monthly income vs expenses. Predict future cash flow and identify potential shortfalls.
              </p>
            </div>
            
            <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                <svg className="w-8 h-8 text-green-600 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 17v.01M7 10v4a5 5 0 0010 0v-4M17 10V7a5 5 0 00-10 0v3" /></svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Bank-level Security
              </h3>
              <p className="text-gray-600">
                Your data is protected with 256-bit encryption and industry best practices.
              </p>
            </div>
            
            <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                <svg className="w-8 h-8 text-green-600 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 014 0l5 2.857A2 2 0 0122 8.857v6.286a2 2 0 01-1 1.714l-5 2.857a4 4 0 01-4 0l-5-2.857A2 2 0 012 15.143V8.857a2 2 0 011-1.714l5-2.857a4 4 0 014 0z" /></svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Data Protection
              </h3>
              <p className="text-gray-600">
                Your data is encrypted and stored offsite. We will never share, sell or rent your personal information. Ever.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-4">
              Simple Setup
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get Started in Minutes
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Three simple steps to start tracking your net worth
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-md p-6 flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mb-4">
                <User className="w-8 h-8 text-brand-600" />
              </div>
              <div className="text-2xl font-bold text-black mb-1">1</div>
              <div className="font-semibold text-[#4ca585]">Create your account</div>
              <div className="text-gray-600 text-sm">Sign up in minutes, no credit card required</div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mb-4">
                <LucideLink className="w-8 h-8 text-brand-600" />
              </div>
              <div className="text-2xl font-bold text-black mb-1">2</div>
              <div className="font-semibold text-[#4ca585]">Connect your accounts & properties</div>
              <div className="text-gray-600 text-sm">Securely link your financial data</div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mb-4">
                <BarChart3 className="w-8 h-8 text-brand-600" />
              </div>
              <div className="text-2xl font-bold text-black mb-1">3</div>
              <div className="font-semibold text-[#4ca585]">See your dashboard</div>
              <div className="text-gray-600 text-sm">Instantly view your net worth and insights</div>
            </div>
          </div>
          
        </div>
      </section>

      {/* Stats Banner Section (restored) */}
      <section className="py-16" style={{ backgroundColor: '#4ca585' }}>
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8 text-center">
            <div className="text-white">
              <div className="text-3xl md:text-4xl font-bold mb-2">
                <AnimatedNumber value={NUMBER_OF_USERS} duration={1800} />
              </div>
              <div className="text-brand-100">Active Users</div>
            </div>
            <div className="text-white">
              <div className="text-3xl md:text-4xl font-bold mb-2">
                <AnimatedNumber value={TOTAL_ASSETS_TRACKED} duration={1800} prefix="$" suffix="+" decimals={1} formatLargeNumbers={true} />
              </div>
              <div className="text-brand-100">Assets Tracked</div>
            </div>
            <div className="text-white">
              <div className="text-3xl md:text-4xl font-bold mb-2">
                <AnimatedNumber value={4.9} duration={1800} suffix="★" decimals={1} />
              </div>
              <div className="text-brand-100">User Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Risk Reversal Section */}
      <section className="py-12 bg-green-50">
        <div className="max-w-4xl mx-auto px-2 sm:px-4 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-brand-100 text-brand-700 rounded-full text-sm font-medium mb-4">
            Guarantee
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            Try SimpleTracker Risk-Free for 30 Days
          </h2>
          <p className="text-base text-gray-600 mb-8 max-w-2xl mx-auto">
            If you don't see a clear improvement in your financial tracking within 30 days, we'll give you a full refund. No questions asked.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 mb-0">
            <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center text-center">
              <img 
                src={process.env.PUBLIC_URL + '/images/guarantee-trial.svg'} 
                alt="Free Trial" 
                className="w-20 h-20 mb-4"
              />
              <div className="font-bold text-lg text-[#4ca585] mb-1">Start your free trial</div>
              <div className="text-gray-700 text-base">No credit card required, instant access</div>
            </div>
            <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center text-center">
              <img 
                src={process.env.PUBLIC_URL + '/images/guarantee-use.svg'} 
                alt="30 Day Usage" 
                className="w-20 h-20 mb-4"
              />
              <div className="font-bold text-lg text-[#4ca585] mb-1">Use SimpleTracker for 30 days</div>
              <div className="text-gray-700 text-base">Full access to all features, no obligation</div>
            </div>
            <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center text-center">
              <img 
                src={process.env.PUBLIC_URL + '/images/guarantee-decide.svg'} 
                alt="Decide" 
                className="w-20 h-20 mb-4"
              />
              <div className="font-bold text-lg text-[#4ca585] mb-1">Decide if it's right for you</div>
              <div className="text-gray-700 text-base">Cancel anytime, no charges, no hassle</div>
            </div>
          </div>
          {/* Compact What You Get in Your Trial List */}
          <div className="mt-6 flex flex-col md:flex-row md:justify-center md:items-center gap-2 md:gap-4">
            <div className="flex items-center text-gray-700 text-sm md:text-base">
              <Check className="w-4 h-4 text-[#4ca585] mr-2 flex-shrink-0" strokeWidth={3} />
              Full access to all features
            </div>
            <div className="flex items-center text-gray-700 text-sm md:text-base">
              <Check className="w-4 h-4 text-[#4ca585] mr-2 flex-shrink-0" strokeWidth={3} />
              Connect unlimited accounts and properties
            </div>
            <div className="flex items-center text-gray-700 text-sm md:text-base">
              <Check className="w-4 h-4 text-[#4ca585] mr-2 flex-shrink-0" strokeWidth={3} />
              AI-powered financial insights
            </div>
            <div className="flex items-center text-gray-700 text-sm md:text-base">
              <Check className="w-4 h-4 text-[#4ca585] mr-2 flex-shrink-0" strokeWidth={3} />
              Priority customer support
            </div>
            <div className="flex items-center text-gray-700 text-sm md:text-base">
              <Check className="w-4 h-4 text-[#4ca585] mr-2 flex-shrink-0" strokeWidth={3} />
              Export and reporting tools
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gradient-to-br from-gray-50 to-green-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-4">
              Pricing
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Choose Your Plan
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Start free, upgrade when you're ready
            </p>
            
            {/* Pricing Toggle */}
            <div className="flex items-center justify-center space-x-4 mb-12">
              <span className={`text-sm font-medium ${!isAnnual ? 'text-gray-900' : 'text-gray-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => setIsAnnual(!isAnnual)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isAnnual ? 'bg-brand-500' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isAnnual ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium ${isAnnual ? 'text-gray-900' : 'text-gray-500'}`}>
                Annual
                {isAnnual && (
                  <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                    Save up to 47%
                  </span>
                )}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {pricingPlans.map((plan, index) => {
              const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
              const monthlyEquivalent = isAnnual ? plan.annualPrice / 12 : plan.monthlyPrice;
              
              return (
                <div
                  key={plan.name}
                  className={`relative bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:transform hover:-translate-y-2 ${
                    plan.popular ? 'ring-2 ring-brand-500 bg-gradient-to-br from-brand-50 to-white' : ''
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-brand-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                        Most Popular
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-gray-900">${price}</span>
                      {plan.monthlyPrice > 0 && (
                        <span className="text-gray-500">
                          {isAnnual ? '/year' : '/month'}
                        </span>
                      )}
                    </div>
                    {isAnnual && plan.monthlyPrice > 0 && (
                      <p className="text-sm text-gray-500">
                        ${monthlyEquivalent.toFixed(0)}/month when billed annually
                      </p>
                    )}
                  </div>
                  
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start group">
                        <Check className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0 group-hover:scale-110 transition-transform" />
                        <span className="text-gray-700 group-hover:text-gray-900 transition-colors">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    onClick={handleGetStarted}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                      plan.popular
                        ? 'bg-brand-500 text-white hover:bg-brand-600'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {plan.cta}
                  </button>
                </div>
              );
            })}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              30-day money-back guarantee • Cancel anytime
            </p>
            <p className="text-sm text-gray-500">
              All plans include our core features. Upgrade or downgrade at any time.
            </p>
          </div>
        </div>
      </section>

      {/* Proof Section - Social Proof */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-4">
              Results
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Real Results from Real Canadians
            </h2>
            <p className="text-xl text-gray-600">
              See how SimpleTracker users are taking control of their finances
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-16">
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 border border-brand-100">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center mr-4">
                  <span className="text-brand-600 font-semibold text-lg">S</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Sarah Chen</h3>
                  <p className="text-gray-600 text-sm">Real Estate Investor</p>
                </div>
              </div>
              <p className="text-gray-700 mb-4 italic">"I was spending 3 hours every Sunday updating spreadsheets. Now I check SimpleTracker once a day and know exactly where I stand. My property portfolio ROI went from unknown to 12.4% - I can finally make data-driven decisions."</p>
              <div className="flex items-center text-sm text-gray-600">
                <span className="text-brand-600 font-semibold">Result:</span>
                <span className="ml-2">Saved 12 hours/month, increased portfolio ROI by 2.1%</span>
              </div>
            </div>
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 border border-brand-100">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center mr-4">
                  <span className="text-brand-600 font-semibold text-lg">M</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Mike Rodriguez</h3>
                  <p className="text-gray-600 text-sm">Small Business Owner</p>
                </div>
              </div>
              <p className="text-gray-700 mb-4 italic">"The AI insights helped me realize I was over-invested in one property. I rebalanced my portfolio and now my net worth is growing 15% faster. The tax optimization tips alone saved me $8,400 last year."</p>
              <div className="flex items-center text-sm text-gray-600">
                <span className="text-brand-600 font-semibold">Result:</span>
                <span className="ml-2">15% faster wealth growth, $8,400 tax savings</span>
              </div>
            </div>
          </div>
        </div>
        {/* Results Banner Section - now full width */}
        <section className="py-12 bg-brand-500">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-bold mb-1 text-white">$47,200</div>
              <div className="text-gray-100 text-sm md:text-base">Avg. net worth increase</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold mb-1 text-white">8.4 hours</div>
              <div className="text-gray-100 text-sm md:text-base">Time saved per month</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold mb-1 text-white">23%</div>
              <div className="text-gray-100 text-sm md:text-base">Avg. portfolio ROI improvement</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold mb-1 text-white">$5,800</div>
              <div className="text-gray-100 text-sm md:text-base">Avg. tax savings</div>
            </div>
          </div>
        </section>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Trusted by
              <span style={{ margin: '0 0.25em' }}>
                <AnimatedNumber value={51862} duration={1200} />
              </span>
              Canadians
            </h2>
            <p className="text-xl text-gray-600">
              See what our users are saying about SimpleTracker
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-green-50 to-brand-50 rounded-2xl p-8 border border-green-100 hover:shadow-lg transition-all duration-300 hover:transform hover:-translate-y-1">
                <div className="flex mb-4">
                  {renderStars(testimonial.rating)}
                </div>
                <p className="text-gray-700 mb-6 italic text-lg">"{testimonial.content}"</p>
                                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                      <span className="text-green-600 font-semibold text-lg">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-gray-600 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about SimpleTracker
            </p>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-100">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-green-50 transition-colors group"
                >
                  <span className="font-semibold text-gray-900 group-hover:text-green-700">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-green-500 transition-transform" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500 group-hover:text-green-500 transition-transform" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 border-t border-green-100">
                    <p className="text-gray-700 pt-4">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - The Close */}
      <section className="py-20" style={{ backgroundColor: '#4ca585' }}>
        <div className="max-w-4xl mx-auto px-2 sm:px-4 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-white text-brand-700 rounded-full text-sm font-medium mb-6">
            Limited Time Offer
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Stop Guessing About Your Net Worth
          </h2>
          <p className="text-xl text-brand-100 mb-6">
            Join <AnimatedNumber value={51862} duration={1200} /> Canadians who have taken control of their financial future
          </p>
          
          <div className="bg-white rounded-2xl p-4 sm:p-8 mb-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">What You'll Get Today:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 text-left">
              <div className="flex items-start">
                <div className="w-7 h-7 bg-brand-500 rounded-full flex items-center justify-center mr-3 mt-1 shadow-md">
                  <Check className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
                <span className="text-gray-700">30-day free trial (no credit card)</span>
              </div>
              <div className="flex items-start">
                <div className="w-7 h-7 bg-brand-500 rounded-full flex items-center justify-center mr-3 mt-1 shadow-md">
                  <Check className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
                <span className="text-gray-700">Full access to Elite features</span>
              </div>
              <div className="flex items-start">
                <div className="w-7 h-7 bg-brand-500 rounded-full flex items-center justify-center mr-3 mt-1 shadow-md">
                  <Check className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
                <span className="text-gray-700">AI-powered financial insights</span>
              </div>
              <div className="flex items-start">
                <div className="w-7 h-7 bg-brand-500 rounded-full flex items-center justify-center mr-3 mt-1 shadow-md">
                  <Check className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
                <span className="text-gray-700">Advanced real estate analytics</span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleGetStarted}
              className="bg-white text-brand-500 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200"
            >
              Start Your Free Trial Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
            <a
              href="#pricing"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-brand-500 transition-colors"
            >
              View All Plans
            </a>
          </div>
          
          <div className="mt-8 space-y-2">
            <p className="text-brand-200 text-sm">
              ⚡ Setup takes less than 5 minutes • 🔒 Bank-level security • 💰 30-day money-back guarantee
            </p>
            <p className="text-brand-200 text-xs">
              Join <AnimatedNumber value={51862} duration={1200} /> Canadians who have increased their net worth by an average of $47,200 in their first year
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}; 